'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useStickyHeaderOffset } from 'components/CardsVerticalScroll/useStickyHeaderOffset';
import { useBreakpoint } from 'hooks/useBreakpoints';
import { usePrefersReducedMotion } from 'hooks/usePrefersReducedMotion';

const SCROLL_KEYS = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ']);

interface PinnedPanel {
  id: string;
  sectionName?: string;
  label: string;
  image: (desktop: boolean) => ReactNode;
  content: (active: boolean) => ReactNode;
}

interface PinnedPanelsLayoutProps {
  panels: PinnedPanel[];
  sectionName?: string;
  label: string;
}

export function PinnedPanelsLayout({ panels, sectionName, label }: PinnedPanelsLayoutProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const selectedIndexRef = useRef<number | null>(null);
  const [canPin, setCanPin] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [topOffset, setTopOffset] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const { isAboveMd: desktop } = useBreakpoint('md');
  const headerHeight = Number.parseFloat(useStickyHeaderOffset() ?? '') || 0;
  const prefersReducedMotion = usePrefersReducedMotion();
  const steps = Math.max(panels.length - 1, 1);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    const updateFit = () => {
      setTopOffset(
        desktop ? headerHeight : Math.min(headerHeight, window.innerHeight - card.getBoundingClientRect().height - 16),
      );
      setIsDesktop(desktop);
      setCanPin(!prefersReducedMotion);
    };
    const observer = new ResizeObserver(updateFit);
    observer.observe(card);
    updateFit();
    window.addEventListener('resize', updateFit);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateFit);
    };
  }, [panels.length, prefersReducedMotion, desktop, headerHeight]);

  useEffect(() => {
    if (!canPin) return;
    let frame = 0;
    const updateActiveIndex = () => {
      const section = sectionRef.current;
      if (!section) return;
      // One viewport of scroll per panel step; the last panel is reached at 90% so it holds before the section unpins.
      const progress = Math.max(
        0,
        Math.min(1, (topOffset - section.getBoundingClientRect().top) / (steps * window.innerHeight * 0.9)),
      );
      // The epsilon stops an exact panel boundary from flooring to the previous panel.
      const index = Math.min(panels.length - 1, Math.floor(progress * steps + 0.000001));
      // While a dot's smooth scroll is in flight, the panels it passes must not take over the selection.
      if (selectedIndexRef.current !== null && index !== selectedIndexRef.current) return;
      selectedIndexRef.current = null;
      setActiveIndex(index);
    };
    const handleScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        updateActiveIndex();
      });
    };
    // Only input that scrolls the page ends a dot's smooth scroll early; a tap or Tab to the new panel's link must not.
    const releaseSelection = () => {
      selectedIndexRef.current = null;
    };
    const releaseOnScrollKey = (event: KeyboardEvent) => {
      if (SCROLL_KEYS.has(event.key)) releaseSelection();
    };
    updateActiveIndex();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('wheel', releaseSelection, { passive: true });
    window.addEventListener('touchmove', releaseSelection, { passive: true });
    window.addEventListener('keydown', releaseOnScrollKey);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('wheel', releaseSelection);
      window.removeEventListener('touchmove', releaseSelection);
      window.removeEventListener('keydown', releaseOnScrollKey);
    };
  }, [canPin, panels.length, steps, topOffset]);

  function handlePanelSelect(index: number) {
    setActiveIndex(index);
    const section = sectionRef.current;
    if (!section || !canPin) return;
    selectedIndexRef.current = index;
    window.scrollTo({
      top:
        window.scrollY +
        section.getBoundingClientRect().top -
        topOffset +
        steps * window.innerHeight * Math.min(1, (index / steps) * 0.9 + 0.01),
      behavior: 'smooth',
    });
  }

  if (panels.length === 0) return null;

  return (
    <section
      id={sectionName}
      ref={sectionRef}
      aria-label={label}
      className="relative bg-soft-black px-6 sm:px-[50px] motion-safe:pb-6 motion-safe:md:pb-0 motion-reduce:py-12">
      {/* The pinned layout is server-rendered from CSS, with the nav height standing in for the measured header, so a
          #hash below this section is already at its final position when the browser jumps to it on load. */}
      <div>
        <div
          className="mx-auto flex max-w-[1440px] items-center motion-safe:sticky motion-safe:top-(--height-nav) max-md:motion-safe:min-h-[calc(100svh-var(--height-nav))] md:motion-safe:h-[calc(100svh-var(--height-nav))]"
          style={
            canPin
              ? {
                  top: topOffset,
                  height: isDesktop ? `calc(100svh - ${topOffset}px)` : undefined,
                  minHeight: isDesktop ? undefined : `calc(100svh - ${Math.max(topOffset, 0)}px)`,
                }
              : undefined
          }>
          <div
            ref={cardRef}
            className="white-colorway relative w-full rounded-[10px] px-6 pb-[88px] pt-6 sm:px-[60px] sm:pb-[108px] sm:pt-[60px] md:px-10 md:pb-[clamp(24px,calc(48svh-264px),108px)] md:pt-[clamp(8px,calc(24svh-136px),60px)] ml:px-[60px]">
            <div className="grid items-start gap-8 md:grid-cols-[0.8fr_1fr] md:items-stretch md:gap-10 ml:gap-[84px]">
              <div className="relative aspect-[1/1.13] min-w-0 overflow-hidden rounded-[10px] md:aspect-auto">
                {panels.map((panel, index) => (
                  <div
                    key={panel.id}
                    aria-hidden={index !== activeIndex}
                    className="absolute inset-0 transition-[clip-path] duration-700 motion-reduce:duration-0 ease-[cubic-bezier(0.76,0,0.24,1)]"
                    style={{
                      zIndex: index,
                      clipPath: index <= activeIndex ? 'inset(0% round 0.75rem)' : 'inset(50% round 0.75rem)',
                    }}>
                    {panel.image(isDesktop)}
                  </div>
                ))}
              </div>
              <div className="grid min-w-0">
                {panels.map((panel, index) => (
                  <div
                    key={panel.id}
                    id={panel.sectionName}
                    aria-hidden={index !== activeIndex}
                    inert={index !== activeIndex}
                    className={`col-start-1 row-start-1 min-w-0 transition-opacity duration-0 ${index === activeIndex ? 'opacity-100' : 'invisible opacity-0 delay-[400ms]'}`}>
                    {panel.content(index === activeIndex)}
                  </div>
                ))}
              </div>
            </div>
            <nav
              className="absolute inset-x-0 bottom-8 flex justify-center sm:bottom-12 md:bottom-[clamp(10px,calc(28svh-156px),48px)] gap-[14px]"
              aria-label={label}>
              {panels.map((panel, index) => (
                <button
                  key={panel.id}
                  type="button"
                  aria-label={panel.label}
                  aria-current={index === activeIndex ? 'step' : undefined}
                  onClick={() => handlePanelSelect(index)}
                  className="relative flex h-2 w-2 items-center justify-center rounded-full after:absolute after:-inset-2 after:content-[''] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-reserve">
                  <span
                    aria-hidden="true"
                    className={`h-2 w-2 rounded-full ${index === activeIndex ? 'scale-[1.15] bg-[#4cc47a]' : 'bg-[#d0d0d0]'}`}
                  />
                </button>
              ))}
            </nav>
          </div>
        </div>
        {/* The pin lasts as long as this spacer, however tall the card is; a fixed-height wrapper left a card taller than the viewport no hold on the last panel. */}
        <div
          aria-hidden="true"
          className="motion-reduce:hidden"
          style={{
            height: `calc(${steps * 100}vh + ${headerHeight ? `${headerHeight}px` : 'var(--height-nav)'})`,
          }}
        />
      </div>
    </section>
  );
}
